import React, { useEffect, useMemo, useState, useCallback } from "react";
import Select from "react-select";
import {
  FaUserCheck,
  FaUserTimes,
  FaUsers,
  FaClock,
  FaSearch,
  FaStore,
  FaFileExcel,
  FaSync,
} from "react-icons/fa";
import { axiosMaster, axiosSalary } from "../api/axiosInstance";
import { useAuth } from "../auth/AuthContext";

/* ─────────────────────────────────────────────
   Helper: is an employee active on a given date?

   Business Rules:
   1. relievingDate = null  → currently working → show
   2. relievingDate has a date → left company → hide after that date
   3. Employee can rejoin: new joiningDate > previous relievingDate
      Each tenure period is checked independently.
   ───────────────────────────────────────────── */
const isActiveOnDate = (emp, reportDate) => {
  const rd = new Date(reportDate);
  rd.setHours(0, 0, 0, 0);

  // Support multi-tenure history if backend sends `employmentHistory` array.
  // Falls back to single joiningDate / relievingDate fields for backward compat.
  const tenures = Array.isArray(emp.employmentHistory) && emp.employmentHistory.length > 0
    ? emp.employmentHistory
    : [{ joiningDate: emp.joiningDate, relievingDate: emp.relievingDate ?? null }];

  return tenures.some((tenure) => {
    if (!tenure.joiningDate) return false;

    const join = new Date(tenure.joiningDate);
    join.setHours(0, 0, 0, 0);

    const relieve = tenure.relievingDate ? new Date(tenure.relievingDate) : null;
    if (relieve) relieve.setHours(0, 0, 0, 0);

    return join <= rd && (!relieve || relieve >= rd);
  });
};

/* ─────────────────────────────────────────────
   CSV escape helper (prevents formula injection)
   ───────────────────────────────────────────── */
const escapeCSV = (val) => {
  const str = String(val ?? "");
  // Prefix cells that start with =, +, -, @ to block Excel injection
  const safe = /^[=+\-@]/.test(str) ? `'${str}` : str;
  return `"${safe.replace(/"/g, '""')}"`;
};

/* ═══════════════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════════════ */
const AtteLogTodayPrecentByGroup = () => {

  /* ─── AUTH ─── */
  const { user } = useAuth();
  const groupId      = user?.groupId;
  const userTypeId   = user?.userTypeId;
  const restaurantId = user?.restaurantId;

  /* ─── STATE ─── */
  const [presentData,        setPresentData]        = useState([]);
  const [employeeData,       setEmployeeData]        = useState([]);
  const [restMap,            setRestMap]             = useState({});
  const [loading,            setLoading]             = useState(false);
  const [search,             setSearch]              = useState("");
  const [activeTab,          setActiveTab]           = useState("total");
  const [lastUpdated,        setLastUpdated]         = useState(null);
  const [restaurantOptions,  setRestaurantOptions]   = useState([]);
  const [selectedRestaurant, setSelectedRestaurant]  = useState(null);

  /* Today's date (used for active-employee filter) */
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  /* ─── FETCH ─── */
  const fetchData = useCallback(async () => {
    if (!groupId) return;
    try {
      setLoading(true);

      const [attRes, restRes, empRes] = await Promise.all([
        axiosSalary.get(`/AttendanceLog/GetPresentTodayByGroup?groupId=${groupId}`),
        axiosMaster.get(`/RestaurantMaster/GetByGroup?groupId=${groupId}`),
        axiosMaster.get(`/EmployeeMaster/GetByGroup?groupId=${groupId}`),
      ]);

      /* --- Present --- */
      let presentList = attRes.data || [];
      if (userTypeId === 4 && restaurantId) {
        presentList = presentList.filter((item) => item.restId === restaurantId);
      }
      setPresentData(presentList);

      /* --- Employees: apply relieving/joining date rule for today --- */
      let empList = (empRes.data || []).filter((emp) => isActiveOnDate(emp, today));
      if (userTypeId === 4 && restaurantId) {
        empList = empList.filter((e) => e.activeRestId === restaurantId);
      }
      setEmployeeData(empList);
      setLastUpdated(new Date());

      /* --- Restaurant map & dropdown --- */
      const map     = {};
      const options = [{ value: 0, label: "All Restaurants" }];
      (restRes.data || []).forEach((r) => {
        map[r.restId] = r.restaurantName;
        options.push({ value: r.restId, label: r.restaurantName });
      });
      setRestMap(map);
      setRestaurantOptions(options);

      setSelectedRestaurant((prev) => prev ?? options[0]);

    } catch (err) {
      console.error("API error:", err);
    } finally {
      setLoading(false);
    }
  }, [groupId, userTypeId, restaurantId, today]);

  /* ─── AUTO-REFRESH every 30 s ─── */
  useEffect(() => {
    if (!groupId) return;
    fetchData();
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") fetchData();
    }, 30000);
    return () => clearInterval(interval);
  }, [groupId, fetchData]);

  /* ─── DERIVED MAPS ─── */
  const empMap = useMemo(() => {
    const map = {};
    employeeData.forEach((e) => { map[e.empId] = e; });
    return map;
  }, [employeeData]);

  const employeeIdSet = useMemo(
    () => new Set(employeeData.map((e) => e.empId)),
    [employeeData]
  );

  /* Unique present IDs that belong to active employees */
  const presentIds = useMemo(() => {
    const set = new Set();
    presentData.forEach((p) => {
      if (employeeIdSet.has(p.empId)) set.add(p.empId);
    });
    return set;
  }, [presentData, employeeIdSet]);

  /* Present rows enriched with employee detail */
  const presentWithDetails = useMemo(() =>
    [...presentIds].map((empId) => {
      const emp   = empMap[empId];
      const punch = presentData.find((p) => p.empId === empId);
      return {
        ...emp,
        empId,
        empName:              emp?.empName,
        empMobileNo:          emp?.empMobileNo || "-",
        activeRestId:         emp?.activeRestId ?? punch?.restId,
        firstPunchDisplayTime: punch?.firstPunchDisplayTime,
      };
    }),
    [presentIds, empMap, presentData]
  );

  const absentData = useMemo(
    () => employeeData.filter((emp) => !presentIds.has(emp.empId)),
    [employeeData, presentIds]
  );

  /* ─── FILTER HELPERS ─── */
  const filterBySearch = (list) => {
    const term = search.toLowerCase().trim();
    if (!term) return list;
    return list.filter(
      (item) =>
        item.empName?.toLowerCase().includes(term) ||
        item.empMobileNo?.includes(term)
    );
  };

  const filterByRestaurant = (list) => {
    if (userTypeId === 4 && restaurantId)
      return list.filter((item) => item.activeRestId === restaurantId);
    if (!selectedRestaurant || selectedRestaurant.value === 0) return list;
    return list.filter((item) => item.activeRestId === selectedRestaurant.value);
  };

  const filteredPresent = filterBySearch(filterByRestaurant(presentWithDetails));
  const filteredAbsent  = filterBySearch(filterByRestaurant(absentData));
  const filteredTotal   = filterBySearch(filterByRestaurant(employeeData));

  /* ─── RESTAURANT CHANGE ─── */
  const handleRestaurantChange = (selected) => {
    setSelectedRestaurant(selected || restaurantOptions[0]);
  };

  /* ─── TAB CHANGE WITH AUTO JUMP ─── */
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  /* ─── EXCEL EXPORT ─── */
  const exportExcel = () => {
    let exportData = [];
    if      (activeTab === "present") exportData = filteredPresent;
    else if (activeTab === "absent")  exportData = filteredAbsent;
    else                               exportData = [...filteredPresent, ...filteredAbsent];
    if (!exportData.length) return;

    const rows = exportData.map((r) => ({
      EmpId:      r.empId,
      Name:       r.empName,
      Mobile:     r.empMobileNo || "-",
      Restaurant: restMap[r.activeRestId] || "",
      // Prefix with tab to prevent Excel from mangling time values
      PunchTime:  r.firstPunchDisplayTime ? `\t${r.firstPunchDisplayTime}` : "",
      Status:     presentIds.has(r.empId) ? "Present" : "Absent",
    }));

    const csv =
      "data:text/csv;charset=utf-8," +
      [
        Object.keys(rows[0]).join(","),
        ...rows.map((row) => Object.values(row).map(escapeCSV).join(",")),
      ].join("\n");

    const link = document.createElement("a");
    link.href     = encodeURI(csv);
    link.download = "AttendanceReport.csv";
    link.click();
  };

  /* ═══════════════════════════════════════════════════
     REUSABLE SUB-COMPONENTS
     ═══════════════════════════════════════════════════ */

  /* Stat card with inline Redo (refresh) button */
  const StatCard = ({ tab, icon: Icon, label, count, activeColor, inactiveColor }) => {
    const isActive = activeTab === tab;
    return (
      <div
        className={`relative p-3 rounded-lg text-center cursor-pointer select-none transition-all ${
          isActive ? `${activeColor} text-white` : inactiveColor
        }`}
        onClick={() => handleTabChange(tab)}
      >
        <Icon className="mx-auto mb-1" />
        <p className="text-sm font-medium">{label}</p>
        <b className="text-lg">{count}</b>

        {/* Redo / Refresh button — top-right of card */}
        <button
          title={`Refresh ${label}`}
          onClick={(e) => {
            e.stopPropagation(); // don't change tab
            fetchData();
          }}
          className={`absolute top-2 right-2 p-1 rounded-full transition-colors ${
            isActive
              ? "text-white/70 hover:text-white hover:bg-white/20"
              : "text-gray-400 hover:text-gray-600 hover:bg-gray-200"
          } ${loading ? "animate-spin" : ""}`}
        >
          <FaSync size={11} />
        </button>
      </div>
    );
  };

  /* Employee card — present */
  const PresentCard = ({ item }) => (
    <div className="border rounded-lg p-3 shadow-sm hover:shadow-md transition bg-white">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
        <div>
          <p className="font-semibold text-sm md:text-base text-slate-800">{item.empName}</p>
          <p className="text-xs text-gray-500">📱 {item.empMobileNo}</p>
          <p className="text-xs text-purple-600 flex items-center gap-1">
            <FaStore />
            {restMap[item.activeRestId] || "Unknown"}
          </p>
        </div>
        <div className="text-blue-600 text-xs sm:text-sm flex items-center gap-1">
          <FaClock />
          {item.firstPunchDisplayTime}
        </div>
      </div>
    </div>
  );

  /* Employee card — absent */
  const AbsentCard = ({ item }) => (
    <div className="border rounded-lg p-3 bg-red-50 shadow-sm">
      <p className="font-semibold text-sm md:text-base text-slate-800">{item.empName}</p>
      <p className="text-xs text-gray-500">📱 {item.empMobileNo}</p>
      <p className="text-xs text-purple-600 flex items-center gap-1">
        <FaStore />
        {restMap[item.activeRestId] || "Unknown"}
      </p>
    </div>
  );

  /* ═══════════════════════════════════════════════════
     RENDER
     ═══════════════════════════════════════════════════ */
  return (
    <div className="bg-white rounded-xl shadow-md p-4 border">

      {/* ── HEADER ── */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-3 mb-4">

        {/* Left */}
        <div>
          <h2 className="text-lg md:text-xl font-bold text-slate-800 flex items-center gap-2">
            <FaUserCheck className="text-green-600" />
            {activeTab === "total"   && "Today's Attendance"}
            {activeTab === "present" && "Present Employees"}
            {activeTab === "absent"  && "Absent Employees"}
          </h2>
          <p className="text-xs text-gray-500">Live attendance · active employees only</p>
          {lastUpdated && (
            <p className="text-xs text-gray-400">
              Updated: {lastUpdated.toLocaleTimeString()}
            </p>
          )}
        </div>

        {/* Right — controls */}
        <div className="flex flex-col sm:flex-row gap-2 w-full lg:w-auto">

          {userTypeId !== 4 && (
            <div className="w-full sm:w-48">
              <Select
                options={restaurantOptions}
                value={selectedRestaurant}
                onChange={handleRestaurantChange}
                placeholder="Restaurant..."
                isClearable
              />
            </div>
          )}

          <div className="relative w-full sm:w-48">
            <FaSearch className="absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-3 py-2 border rounded-lg text-sm w-full"
            />
          </div>

          <button
            onClick={exportExcel}
            className="bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-lg flex items-center justify-center gap-2 w-full sm:w-auto"
          >
            <FaFileExcel /> Excel
          </button>

        </div>
      </div>

      {/* ── STAT CARDS (with per-card Redo buttons) ── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <StatCard
          tab="total"
          icon={FaUsers}
          label="Total"
          count={filteredTotal.length}
          activeColor="bg-blue-500"
          inactiveColor="bg-blue-100"
        />
        <StatCard
          tab="present"
          icon={FaUserCheck}
          label="Present"
          count={filteredPresent.length}
          activeColor="bg-green-500"
          inactiveColor="bg-green-100"
        />
        <StatCard
          tab="absent"
          icon={FaUserTimes}
          label="Absent"
          count={filteredAbsent.length}
          activeColor="bg-red-500"
          inactiveColor="bg-red-100"
        />
      </div>

      {/* ── LOADING / EMPTY ── */}
      {loading && <p className="text-center py-6 text-gray-500">Loading...</p>}
      {!loading && filteredTotal.length === 0 && (
        <p className="text-center py-6 text-gray-500">No active employees found</p>
      )}

      {/* ── TOTAL VIEW ── */}
      {!loading && activeTab === "total" && (
        <>
          <div className="mb-4">
            <h3 className="text-green-700 font-semibold mb-2 border-b pb-1">
              ✅ Present Employees ({filteredPresent.length})
            </h3>
            <div className="space-y-2">
              {filteredPresent.map((item) => (
                <PresentCard key={item.empId} item={item} />
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-red-700 font-semibold mb-2 border-b pb-1">
              ❌ Absent Employees ({filteredAbsent.length})
            </h3>
            <div className="space-y-2">
              {filteredAbsent.map((item) => (
                <AbsentCard key={item.empId} item={item} />
              ))}
            </div>
          </div>
        </>
      )}

      {/* ── PRESENT TAB ── */}
      {!loading && activeTab === "present" && (
        <div className="space-y-2">
          {filteredPresent.map((item) => (
            <PresentCard key={item.empId} item={item} />
          ))}
          {filteredPresent.length === 0 && (
            <p className="text-center py-4 text-gray-400">No present employees</p>
          )}
        </div>
      )}

      {/* ── ABSENT TAB ── */}
      {!loading && activeTab === "absent" && (
        <div className="space-y-2">
          {filteredAbsent.map((item) => (
            <AbsentCard key={item.empId} item={item} />
          ))}
          {filteredAbsent.length === 0 && (
            <p className="text-center py-4 text-gray-400">No absent employees</p>
          )}
        </div>
      )}

    </div>
  );
};

export default AtteLogTodayPrecentByGroup;